import { Controller } from "@hotwired/stimulus"

/**
 * IoT Impact Level Visibility editor (CONEK-9318).
 *
 * The admin page used to be one card and one Save per row: with five sites and
 * twenty-five buildings that is thirty buttons and thirty round trips. The
 * page now submits ONE batch (the sticky bar) and this controller owns the
 * three things a static render cannot answer:
 *
 *   1. THE DIRTY SET. Every row's state is a set of hidden levels, or `null`
 *      for "inherits from the level above". `initial` is what the server
 *      rendered; `states` is what the operator currently means. A row is sent
 *      only when those differ — the hidden fields of every other row stay
 *      disabled, and a disabled control is not submitted.
 *
 *   2. INHERITANCE, LIVE. A child row with no rule of its own displays its
 *      nearest ancestor's levels, ticked but disabled. Editing a parent updates
 *      every inherited descendant IMMEDIATELY (as a preview; the row itself is
 *      not dirty until saved), which is what the old page could not show: after
 *      ticking a branch, its buildings still looked empty until a reload.
 *
 *   3. NAVIGATION. Search, a filter ("only overridden" / "only different from
 *      inherited") and expand/collapse-all, so a large estate is scannable
 *      without opening thirty cards.
 *
 * Markup contract (iot/admin/impact_level_visibilities/index.html.erb and its
 * partials): rows are `[data-iot--impact-visibility-target="row"]` fieldsets
 * carrying scope/scope_id/site_id/building_id, their own name, and their
 * initial state. Sites are `[data-role="site-card"]` accordions; buildings
 * declare which site they belong to via `data-site-id`; asset groups live in
 * `[data-role="asset-group-row"]` wrappers inside their building and declare
 * their building via `data-building-id`.
 */
export default class extends Controller {
  static targets = ["row"]
  static values = {
    inheritedTemplate: String,
    inheritedEmptyTemplate: String,
    ownTemplate: String,
    ownEmptyTemplate: String,
    editingOverrideTemplate: String,
    pendingResetTemplate: String,
    accountDefaultTemplate: String,
    siteCountsOne: String,
    siteCountsOther: String,
    unsavedLabel: String,
    changesTemplate: String,
    leaveConfirm: String,
  }

  // The severity order the server renders, so a live re-render cannot reorder
  // "Info, Advisory, Warning, Critical".
  static LEVELS = ["info", "advisory", "warning", "critical"]

  connect() {
    this.initial = new Map()
    this.states = new Map()
    this.pendingReset = new Set()
    this.query = ""
    this.filter = "all"

    this.rowTargets.forEach((row) => {
      const initial = this.initialStateFor(row)
      this.initial.set(row.dataset.rowKey, initial)
      this.states.set(
        row.dataset.rowKey,
        initial === null ? null : new Set(initial),
      )
    })

    // Leaving with a batch in flight silently throws it away. The account
    // picker carries its own guard; this covers the browser's back/close.
    this.beforeUnload = (event) => {
      if (this.dirtyKeys().length === 0) return
      event.preventDefault()
      event.returnValue = this.leaveConfirmValue
    }
    window.addEventListener("beforeunload", this.beforeUnload)

    this.refresh()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnload)
  }

  // --- operator actions -----------------------------------------------------

  // "Override here": take the inherited value as a draft rule of this row's
  // own. It does NOT copy the parent's value into the form and let the operator
  // save by accident — the row becomes dirty by the very act of overriding, and
  // the state line says saving makes it independent.
  override(event) {
    const key = event.currentTarget.dataset.rowKey
    const row = this.rowByKey(key)
    if (!row) return

    // A pending reset is showing the parent's values; Override undoes it back
    // to the row's own stored rule rather than adopting the parent's.
    if (this.pendingReset.has(key)) {
      this.pendingReset.delete(key)
      const initial = this.initial.get(key)
      this.states.set(key, initial === null ? null : new Set(initial))
      this.refresh()
      return
    }

    this.states.set(key, new Set(this.effective(this.parentKey(row)).levels))
    this.openSiteOf(row)
    this.refresh()
  }

  // "Reset to inherited": the row's own rule goes (a DELETE in the batch).
  // On a draft override that was never saved, this is simply "cancel".
  reset(event) {
    const key = event.currentTarget.dataset.rowKey
    if (!this.states.has(key)) return

    if (this.pendingReset.has(key)) {
      // Undo the pending reset: back to the stored rule.
      this.pendingReset.delete(key)
      const initial = this.initial.get(key)
      this.states.set(key, initial === null ? null : new Set(initial))
      this.refresh()
      return
    }

    this.states.set(key, null)
    if (this.initial.get(key) !== null) this.pendingReset.add(key)
    this.refresh()
  }

  levelChanged(event) {
    const row = event.target.closest(
      '[data-iot--impact-visibility-target="row"]',
    )
    if (!row) return

    const key = row.dataset.rowKey
    if (this.states.get(key) === null) return

    this.states.set(key, new Set(this.checkedLevels(row)))
    this.refresh()
  }

  discard() {
    this.pendingReset.clear()
    this.initial.forEach((initial, key) => {
      this.states.set(key, initial === null ? null : new Set(initial))
    })
    this.refresh()
  }

  // The browser submits every named control that is not disabled, so the batch
  // is narrowed here to the dirty rows. The action field distinguishes a reset
  // (DELETE) from the real empty set ("show every level here").
  prepareSubmit(event) {
    const dirty = new Set(this.dirtyKeys())
    if (dirty.size === 0) {
      event.preventDefault()
      return
    }

    this.rowTargets.forEach((row) => {
      const sent = dirty.has(row.dataset.rowKey)
      row.querySelectorAll("[data-row-field]").forEach((field) => {
        field.disabled = !sent
      })
    })
  }

  guardAccountChange(event) {
    if (this.dirtyKeys().length === 0) return
    if (window.confirm(this.leaveConfirmValue)) return

    event.preventDefault()
  }

  // --- search, filter, expand ----------------------------------------------

  search(event) {
    this.query = event.target.value.trim().toLowerCase()
    this.applyFilters()
  }

  filterChanged(event) {
    this.filter = event.target.value || "all"
    this.applyFilters()
  }

  expandAll() {
    this.accordionTriggers().forEach((trigger) => {
      if (trigger.getAttribute("aria-expanded") !== "true") trigger.click()
    })
  }

  collapseAll() {
    this.accordionTriggers().forEach((trigger) => {
      if (trigger.getAttribute("aria-expanded") === "true") trigger.click()
    })
  }

  // --- rendering ------------------------------------------------------------

  refresh() {
    this.rowTargets.forEach((row) => this.renderRow(row))
    this.renderSiteCounts()
    this.renderBatchBar()
    this.applyFilters()
  }

  renderRow(row) {
    const key = row.dataset.rowKey
    const state = this.states.get(key)
    const effective = this.effective(key)
    const displayed = state === null ? effective.levels : state
    const dirty = this.isDirty(key)

    row.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = displayed.has(input.value)
      this.setDisabled(input, state === null)
    })

    row.querySelectorAll("[data-row-field]").forEach((field) => {
      field.disabled = !dirty
    })

    const action = row.querySelector('[data-role="action"]')
    if (action) action.value = this.pendingReset.has(key) ? "delete" : "save"

    this.stateElementFor(row).textContent = this.stateText(
      row,
      state,
      effective,
    )

    const badge = this.dirtyBadgeFor(row)
    if (badge) badge.hidden = !dirty

    this.renderRowActions(row, state)
  }

  renderRowActions(row, state) {
    const container = this.actionContainerFor(row)
    if (!container) return

    const override = container.querySelector(
      `[data-role="override-button"][data-row-key="${row.dataset.rowKey}"]`,
    )
    const reset = container.querySelector(
      `[data-role="reset-button"][data-row-key="${row.dataset.rowKey}"]`,
    )

    if (override) override.hidden = state !== null
    if (reset)
      reset.hidden =
        row.dataset.scope === "account"
          ? row.dataset.initialState !== "own"
          : state === null
  }

  renderSiteCounts() {
    this.siteCards().forEach((card) => {
      const siteRow = this.rowByKey(card.dataset.siteKey)
      if (!siteRow) return

      const buildings = this.buildingRowsFor(card.dataset.siteKey)
      const overridden = buildings.filter(
        (row) => this.states.get(row.dataset.rowKey) !== null,
      ).length
      const target = card.querySelector('[data-role="site-counts"]')
      if (!target) return

      const template =
        buildings.length === 1
          ? this.siteCountsOneValue
          : this.siteCountsOtherValue
      target.textContent = this.format(template, {
        overridden,
        total: buildings.length,
      })
    })
  }

  renderBatchBar() {
    const count = this.dirtyKeys().length
    const bar = this.element.querySelector('[data-role="batch-bar"]')
    if (!bar) return

    bar.hidden = count === 0
    const label = bar.querySelector('[data-role="dirty-count"]')
    if (label) {
      label.textContent =
        count === 0 ? "" : this.format(this.pluralTemplate(count), { count })
    }
  }

  // The locale's plural forms arrive as JSON ({one, few, many, other, …}) and
  // the form is chosen with the page's own plural rule, so a language that
  // distinguishes "2 zmiany" from "5 zmian" says the right thing instead of
  // forcing every count into "other".
  pluralTemplate(count) {
    const forms = this.pluralForms()
    let rule
    try {
      const locale =
        this.element.closest("[lang]")?.lang ||
        document.documentElement.lang ||
        "en"
      rule = new Intl.PluralRules(locale).select(count)
    } catch {
      rule = count === 1 ? "one" : "other"
    }
    return forms[rule] || forms.other || forms.one || ""
  }

  pluralForms() {
    try {
      return JSON.parse(this.changesTemplateValue) || {}
    } catch {
      return {}
    }
  }

  applyFilters() {
    let anyVisible = false

    this.siteCards().forEach((card) => {
      const siteRow = this.rowByKey(card.dataset.siteKey)
      if (!siteRow) return

      const siteNameMatches =
        this.query === "" ||
        siteRow.dataset.rowName.toLowerCase().includes(this.query)
      const buildings = this.buildingRowsFor(card.dataset.siteKey)
      let visibleBuildings = 0

      buildings.forEach((row) => {
        const wrapper = row.closest('[data-role="building-row"]')
        const buildingNameMatches =
          this.query === "" ||
          siteNameMatches ||
          row.dataset.rowName.toLowerCase().includes(this.query)
        let visibleGroups = 0

        // An asset group is a row of its own: it can match the search or the
        // filter even when its building's row does not, and a building with a
        // matching group must stay visible (otherwise the operator filters to
        // "overridden" and the override they are looking for disappears).
        this.assetGroupRowsFor(row).forEach((groupRow) => {
          const groupWrapper = groupRow.closest('[data-role="asset-group-row"]')
          const groupNameMatches =
            buildingNameMatches ||
            groupRow.dataset.rowName.toLowerCase().includes(this.query)
          const show = groupNameMatches && this.matchesFilter(groupRow)
          if (groupWrapper) groupWrapper.hidden = !show
          // A search for a group name should land on its levels, not on a
          // closed header the operator has to open by hand.
          if (show && this.query !== "" && groupWrapper)
            this.openAccordion(groupWrapper)
          if (show) visibleGroups += 1
        })

        const show =
          (buildingNameMatches && this.matchesFilter(row)) || visibleGroups > 0
        if (wrapper) wrapper.hidden = !show
        if (show) visibleBuildings += 1
      })

      // A site shows when it matches the filter itself, or when one of its
      // buildings does. A site whose own row is inherited still shows if it
      // CONTAINS an override — that is what the operator is looking for.
      const showSite =
        (this.matchesFilter(siteRow) &&
          (this.query === "" || siteNameMatches || visibleBuildings > 0)) ||
        visibleBuildings > 0

      card.hidden = !showSite
      if (showSite) {
        anyVisible = true
        if (this.query !== "" && !siteNameMatches) this.openAccordion(card)
      }
    })

    const empty = this.element.querySelector('[data-role="no-results"]')
    if (empty) empty.hidden = anyVisible || this.siteCards().length === 0
  }

  // --- state helpers --------------------------------------------------------

  isDirty(key) {
    const initial = this.initial.get(key)
    const state = this.states.get(key)

    if (initial === null && state === null) return false
    if (initial === null || state === null) return true
    return !this.sameSet(initial, state)
  }

  dirtyKeys() {
    return this.rowTargets
      .map((row) => row.dataset.rowKey)
      .filter((key) => this.isDirty(key))
  }

  // What a row actually hides: its own state, or the nearest ancestor that has
  // one. The account root always has a state (possibly the empty set), so the
  // recursion terminates there.
  effective(key) {
    const row = this.rowByKey(key)
    if (!row) return { levels: new Set(), name: "" }

    const state = this.states.get(key)
    if (state !== null && state !== undefined) {
      return { levels: new Set(state), name: row.dataset.rowName }
    }

    const parent = this.parentKey(row)
    if (!parent) return { levels: new Set(), name: row.dataset.rowName }

    return this.effective(parent)
  }

  parentKey(row) {
    if (row.dataset.scope === "account") return null
    if (row.dataset.scope === "asset_group")
      return `building-${row.dataset.buildingId}`
    if (row.dataset.scope === "building") return `site-${row.dataset.siteId}`
    return "account"
  }

  stateText(row, state, effective) {
    const key = row.dataset.rowKey
    const levels = this.levelsLabel(
      row,
      state === null ? effective.levels : state,
    )
    // The row it inherits FROM, as the server names it: the direct parent.
    // The VALUE may come from further up (a site with no rule of its own), but
    // the line names the level in the tree the operator is looking at, which is
    // what the server-rendered text says too.
    const parentName = this.parentName(row)

    if (this.pendingReset.has(key)) {
      return row.dataset.scope === "account"
        ? this.accountDefaultTemplateValue
        : this.format(this.pendingResetTemplateValue, { name: parentName })
    }

    // The account root with no stored rule reads as the account default — the
    // same sentence the server rendered. Once the operator departs from it
    // (dirty), the row is describing its own draft rule instead.
    if (
      row.dataset.scope === "account" &&
      row.dataset.initialState === "default" &&
      !this.isDirty(key)
    ) {
      return this.accountDefaultTemplateValue
    }

    if (state !== null && state !== undefined) {
      // A row that had no rule until "Override here" is a NEW override, and
      // the line says so — saving it makes the rule independent of the level
      // above. The account root never inherits, so it is just its own rule.
      if (
        row.dataset.initialState !== "own" &&
        row.dataset.scope !== "account"
      ) {
        return this.format(this.editingOverrideTemplateValue, {
          name: parentName,
        })
      }

      return levels
        ? this.format(this.ownTemplateValue, { levels })
        : this.ownEmptyTemplateValue
    }

    return levels
      ? this.format(this.inheritedTemplateValue, { name: parentName, levels })
      : this.format(this.inheritedEmptyTemplateValue, { name: parentName })
  }

  parentName(row) {
    const key = this.parentKey(row)
    const parent = key ? this.rowByKey(key) : null
    return parent ? parent.dataset.rowName : ""
  }

  levelsLabel(row, levels) {
    const set = levels instanceof Set ? levels : new Set(levels)
    return this.constructor.LEVELS.filter((level) => set.has(level))
      .map((level) => this.levelLabel(row, level))
      .filter(Boolean)
      .join(", ")
  }

  levelLabel(row, level) {
    const input = row.querySelector(`input[type="checkbox"][value="${level}"]`)
    if (!input) return ""
    const label = document.querySelector(`label[for="${input.id}"]`)
    return label ? label.textContent.trim() : level
  }

  // --- DOM lookups ----------------------------------------------------------

  setDisabled(input, disabled) {
    input.disabled = disabled
  }

  checkedLevels(row) {
    return Array.from(
      row.querySelectorAll('input[type="checkbox"]:checked'),
    ).map((input) => input.value)
  }

  initialStateFor(row) {
    const levels = row.dataset.initialLevels
      ? row.dataset.initialLevels.split(",")
      : []
    if (row.dataset.initialState === "own" || row.dataset.scope === "account") {
      return new Set(levels)
    }
    return null
  }

  rowByKey(key) {
    return this.rowTargets.find((row) => row.dataset.rowKey === key)
  }

  siteCards() {
    return Array.from(this.element.querySelectorAll('[data-role="site-card"]'))
  }

  siteCardFor(row) {
    return row.closest('[data-role="site-card"]')
  }

  buildingRowsFor(siteKey) {
    return Array.from(
      this.element.querySelectorAll('[data-role="building-row"]'),
    )
      .filter((wrapper) => wrapper.dataset.siteKey === siteKey)
      .map((wrapper) =>
        wrapper.querySelector('[data-iot--impact-visibility-target="row"]'),
      )
      .filter(Boolean)
  }

  // The asset-group rows inside one building row. Scoped to the building's own
  // wrapper, so a group can never be attributed to a sibling building.
  assetGroupRowsFor(buildingRow) {
    const wrapper = buildingRow.closest('[data-role="building-row"]')
    if (!wrapper) return []

    return Array.from(wrapper.querySelectorAll('[data-role="asset-group-row"]'))
      .map((groupWrapper) =>
        groupWrapper.querySelector(
          '[data-iot--impact-visibility-target="row"]',
        ),
      )
      .filter(Boolean)
  }

  stateElementFor(row) {
    if (row.dataset.scope === "site") {
      const card = this.siteCardFor(row)
      return card ? card.querySelector('[data-role="site-state"]') : row
    }
    return row.parentElement.querySelector('[data-role="state"]') || row
  }

  // Asset-group rows are accordions now (CONEK-9318): their badge and actions
  // live in the header, outside the row's own parent (the collapsible body), so
  // they are found on the group's wrapper instead.
  groupWrapperFor(row) {
    if (row.dataset.scope !== "asset_group") return null
    return row.closest('[data-role="asset-group-row"]')
  }

  dirtyBadgeFor(row) {
    const container =
      row.dataset.scope === "site"
        ? this.siteCardFor(row)
        : this.groupWrapperFor(row) || row.parentElement
    return container
      ? container.querySelector('[data-role="dirty-badge"]')
      : null
  }

  actionContainerFor(row) {
    if (row.dataset.scope === "site") return this.siteCardFor(row)
    return this.groupWrapperFor(row) || row.parentElement
  }

  accordionTriggers() {
    return Array.from(
      this.element.querySelectorAll('[data-ui--accordion-target="trigger"]'),
    )
  }

  openSiteOf(row) {
    const card = this.siteCardFor(row)
    if (card) this.openAccordion(card)
    // A group row is itself an accordion: opening its site would still leave
    // the override control's result hidden behind a closed group header.
    const group = this.groupWrapperFor(row)
    if (group) this.openAccordion(group)
  }

  openAccordion(card) {
    const trigger = card.querySelector('[data-ui--accordion-target="trigger"]')
    if (trigger && trigger.getAttribute("aria-expanded") !== "true")
      trigger.click()
  }

  matchesFilter(row) {
    if (this.filter === "all") return true

    const key = row.dataset.rowKey
    const state = this.states.get(key)

    if (this.filter === "overridden")
      return state !== null && state !== undefined

    // "different": has a value that departs from what it would inherit.
    if (this.pendingReset.has(key)) return true
    if (state === null || state === undefined) return false

    const parent = this.effective(this.parentKey(row))
    return !this.sameSet(state, parent.levels)
  }

  // --- small utilities ------------------------------------------------------

  sameSet(left, right) {
    if (left.size !== right.size) return false
    for (const item of left) {
      if (!right.has(item)) return false
    }
    return true
  }

  format(template, values) {
    return String(template || "").replace(/%\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(values, name)
        ? String(values[name])
        : match,
    )
  }
}
